// Lightweight, dependency-free sound engine. All cues are synthesized live
// with the Web Audio API (no asset files to bundle), shaped with proper
// attack/decay envelopes so they feel warm and premium rather than beepy.
//
// Usage:  import { sound } from "@/lib/sound";  sound.play("success");
// Respects a persisted mute flag and the browser's autoplay gesture rule.

const STORE_KEY = "hp.muted";

let ctx = null;
let master = null;
let unlocked = false;

const isMuted = () => localStorage.getItem(STORE_KEY) === "1";

function ensureCtx() {
  if (typeof window === "undefined") return null;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  if (!ctx) {
    ctx = new AC();
    master = ctx.createGain();
    master.gain.value = 0.9;
    master.connect(ctx.destination);
  }
  if (ctx.state === "suspended") ctx.resume().catch(() => {});
  return ctx;
}

// A single voice: oscillator → gain envelope → (optional) lowpass → master.
function voice({ type = "sine", from, to, dur, gain = 0.12, delay = 0, glideDur, cutoff }) {
  const c = ensureCtx();
  if (!c) return;
  const t0 = c.currentTime + delay;
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(from, t0);
  if (to && to !== from) {
    osc.frequency.exponentialRampToValueAtTime(to, t0 + (glideDur ?? dur));
  }

  let node = osc;
  if (cutoff) {
    const lp = c.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = cutoff;
    osc.connect(lp);
    node = lp;
  }

  // Fast attack, smooth exponential release — reads as "soft" not "clicky".
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(gain, t0 + 0.012);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);

  node.connect(g);
  g.connect(master);
  osc.start(t0);
  osc.stop(t0 + dur + 0.05);
}

const CUES = {
  // Soft UI tick for primary taps.
  tap: () => voice({ type: "sine", from: 540, to: 620, dur: 0.07, gain: 0.05, glideDur: 0.03 }),
  // Bright little pop for claiming an item.
  pop: () => voice({ type: "triangle", from: 720, to: 1180, dur: 0.09, gain: 0.06, glideDur: 0.05 }),
  // Subtle navigation whoosh.
  nav: () => voice({ type: "sine", from: 380, to: 300, dur: 0.12, gain: 0.035, cutoff: 900 }),
  // Warm ascending triad — payment confirmed / bill funded.
  success: () => {
    voice({ type: "sine", from: 523.25, to: 523.25, dur: 0.5, gain: 0.11, delay: 0 });
    voice({ type: "sine", from: 659.25, to: 659.25, dur: 0.55, gain: 0.1, delay: 0.09 });
    voice({ type: "sine", from: 783.99, to: 783.99, dur: 0.7, gain: 0.095, delay: 0.18 });
    voice({ type: "triangle", from: 1046.5, to: 1046.5, dur: 0.6, gain: 0.04, delay: 0.18 });
  },
  // Money landed — a rounder, richer version for settlement.
  cash: () => {
    voice({ type: "sine", from: 660, to: 990, dur: 0.14, gain: 0.09, glideDur: 0.08 });
    voice({ type: "sine", from: 990, to: 1320, dur: 0.4, gain: 0.07, delay: 0.11 });
  },
  // Gentle low descending tone for errors.
  error: () => {
    voice({ type: "sine", from: 260, to: 150, dur: 0.28, gain: 0.09, cutoff: 700 });
  },
};

export const sound = {
  play(name) {
    if (isMuted()) return;
    const cue = CUES[name];
    if (cue) {
      try {
        cue();
      } catch {
        /* audio not available — silent no-op */
      }
    }
  },
  isMuted,
  toggleMuted() {
    const next = !isMuted();
    localStorage.setItem(STORE_KEY, next ? "1" : "0");
    if (!next) {
      ensureCtx();
      this.play("tap");
    }
    return next;
  },
};

// Unlock the audio context on the very first user gesture so the first
// meaningful cue (e.g. a payment) always plays.
function unlock() {
  if (unlocked) return;
  unlocked = true;
  ensureCtx();
  window.removeEventListener("pointerdown", unlock);
  window.removeEventListener("keydown", unlock);
}
if (typeof window !== "undefined") {
  window.addEventListener("pointerdown", unlock, { once: false });
  window.addEventListener("keydown", unlock, { once: false });
}
